import type { ApprovalWorkflow } from '@/types/workflow';

const STORAGE_KEY = 'timetrack_workflows';

/**
 * Get all workflows from localStorage
 */
export function getStoredWorkflows(): ApprovalWorkflow[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const workflows = JSON.parse(stored);
    
    // Convert date strings back to Date objects
    return workflows.map((w: any) => ({
      ...w,
      createdAt: new Date(w.createdAt),
      updatedAt: w.updatedAt ? new Date(w.updatedAt) : undefined,
      stages: w.stages?.map((s: any) => ({
        ...s,
        createdAt: new Date(s.createdAt),
        updatedAt: s.updatedAt ? new Date(s.updatedAt) : undefined,
        approvers: s.approvers?.map((a: any) => ({
          ...a,
          createdAt: new Date(a.createdAt),
          updatedAt: a.updatedAt ? new Date(a.updatedAt) : undefined,
        })),
      })),
    }));
  } catch (error) {
    console.error('Failed to load workflows from localStorage:', error);
    return [];
  }
}

/**
 * Save workflows to localStorage
 */
export function saveWorkflows(workflows: ApprovalWorkflow[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(workflows));
  } catch (error) {
    console.error('Failed to save workflows to localStorage:', error);
  }
}

/**
 * Add a new workflow to localStorage
 */
export function addWorkflow(workflow: ApprovalWorkflow): void {
  const workflows = getStoredWorkflows();
  workflows.push(workflow);
  saveWorkflows(workflows);
}

/**
 * Update an existing workflow in localStorage
 */
export function updateWorkflow(id: string, updates: Partial<ApprovalWorkflow>): ApprovalWorkflow | null {
  const workflows = getStoredWorkflows();
  const index = workflows.findIndex(w => w.id === id);
  
  if (index === -1) return null;
  
  workflows[index] = {
    ...workflows[index],
    ...updates,
    updatedAt: new Date(),
    version: workflows[index].version + 1,
  };
  
  saveWorkflows(workflows);
  return workflows[index];
}

/**
 * Delete a workflow from localStorage
 */
export function deleteWorkflowFromStorage(id: string): boolean {
  const workflows = getStoredWorkflows();
  const index = workflows.findIndex(w => w.id === id);
  
  if (index === -1) return false;
  
  const workflow = workflows[index];
  if (workflow.isInUse || workflow.isDefault) {
    return false;
  }
  
  workflows.splice(index, 1);
  saveWorkflows(workflows);
  return true;
}

/**
 * Clear all workflows from localStorage
 */
export function clearWorkflows(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Check if localStorage has any workflows
 */
export function hasStoredWorkflows(): boolean {
  return getStoredWorkflows().length > 0;
}
